import { belagNotiz } from './surface'
import { stellenZuordnung, tulipStelle } from './tulipMerge'
import type { SerpentineRun, SurfaceSection, Tulip, TulipShape } from './types'

/**
 * Aus den Abbiegeanweisungen der Strecke werden Tulpen - vorgeschlagen, nicht
 * gesetzt.
 *
 * Das Problem, das dieses Modul löst: die Notation ist da, die Legende ist da,
 * der Druck ist da - und niemand kann sie befüllen. Von Hand hiesse, jede
 * Kreuzung einer 220-Kilometer-Etappe abzutippen, samt Kilometerstand, den vor
 * der Fahrt niemand kennt. Eine Funktion, die niemand befüllen kann, ist
 * Dekoration.
 *
 * Die Quelle ist der Routendienst selbst. OSRM nennt zu jedem Manöver die
 * Winkel *aller* Strassen an der Kreuzung samt der Auskunft, welche davon
 * befahrbar sind (intersections[].bearings und .entry), dazu die Art des
 * Manövers, die Ausfahrtnummer am Kreisverkehr und die Beschilderung. Das ist
 * genau das, was eine Tulpe zeichnet: die Arme, den Pfeil, den Punkt, aus dem
 * man kommt. Die Kreuzung *hat* ihre Arme, und der Dienst nennt ihre Winkel -
 * die Tulpe wird also gezeichnet und nicht geschätzt.
 *
 * Die eigentliche Arbeit ist nicht die Umrechnung, sondern die Auswahl. Eine
 * Etappe hat bis zu 123 Manöver (Sinemorets - Sibiu, 900 km). Ein Roadbook, das
 * jedes zeigt, ist wertlos: der Sinn der Notation ist, die *wenigen* Stellen zu
 * markieren, an denen man sich verfährt. Was hier weggeworfen wird, steht bei
 * jeder Regel dabei.
 *
 * Vier Quellen, nicht eine. Die Manöver sind die erste; Kehren, Tunnel und
 * Belag kommen aus der Geometrie, aus dem eigenen Kachelarchiv und aus
 * OpenStreetMap (RouteFeatures), weil sie in keiner Abbiegeanweisung stehen.
 * Die letzte steht ebenfalls in den Manövern, nur nicht dort, wo man sie sucht:
 * eine Fähre ist am Modus des Schritts zu erkennen und nicht an der Art des
 * Manövers.
 *
 * Reine Funktionen, kein fetch und kein React: die Regeln lassen sich damit an
 * echten Antworten des Dienstes prüfen (tulipSuggest.test.ts), und das ist bei
 * einer Auswahl, die man nicht ansieht, die einzige Art, sie zu verantworten.
 */

/**
 * Ein Manöver, so wie der Server es durchreicht.
 *
 * Die Feldnamen sind die von OSRM, weil sie hier gedeutet werden und weil eine
 * eigene Benennung nur eine zweite Sprache für dieselbe Sache wäre. Was der
 * Server dabei weglässt, steht in backend/internal/routing/routing.go.
 */
export interface RouteStep {
  /** Länge dieses Abschnitts in Metern, also vom Manöver bis zum nächsten. */
  distance: number
  /** Art des Manövers, etwa "turn", "roundabout", "off ramp". */
  type: string
  /**
   * Art der Fortbewegung auf diesem Abschnitt, meist "driving".
   *
   * Der eine Wert, auf den es ankommt, ist "ferry". Er ist die einzige
   * verlässliche Auskunft über eine Fähre: die Art des Manövers hilft nicht,
   * an einer Fähre steht dort mal "notification" und mal "turn", und beides
   * heisst anderswo etwas ganz anderes.
   */
  mode?: string
  /** Seite dazu, etwa "left" oder "slight right". */
  modifier?: string
  /** Nummer der Ausfahrt am Kreisverkehr, von der Einfahrt an gezählt. */
  exit?: number
  /** Name der Strasse nach dem Manöver. */
  name?: string
  /** Nummer der Strasse nach dem Manöver, etwa "A2" oder "209;E61". */
  ref?: string
  /** Was auf dem Wegweiser steht, etwa "Nin, Smilčić, Zadar - zapad". */
  destinations?: string
  /** Fahrtrichtung vor dem Manöver, in Grad; 0 ist Nord. */
  bearingBefore: number
  /** Fahrtrichtung nach dem Manöver. */
  bearingAfter: number
  /** Winkel aller Strassen am Knoten - die Arme der Tulpe. */
  bearings?: number[]
  /** Je Arm: darf man dort hineinfahren? */
  entry?: boolean[]
  /** Stelle in bearings, aus der man kommt. -1 heisst "nicht angegeben". */
  in?: number
  /** Stelle in bearings, in die man fährt. -1 heisst "nicht angegeben". */
  out?: number
}

/**
 * Ein Tunnel auf der Strecke, so wie der Server ihn meldet.
 *
 * Er stammt nicht aus der Antwort des Routendienstes, sondern aus dem eigenen
 * Kachelarchiv: die Protomaps-Basiskarte führt in ihrer Strassenebene das
 * Merkmal `is_tunnel`, und dieselbe Datei, die die Karte zeichnet, beantwortet
 * damit die Frage, wo es dunkel wird. Gelesen wird sie im Server
 * (backend/internal/basemap), weil die Datei dort liegt und weil die Antwort
 * vollständig sein muss - im Browser hinge sie daran, welche Kacheln gerade
 * geladen sind.
 */
export interface RouteTunnel {
  /** Kilometerstand des Eingangs, ab Beginn der Etappe. */
  km: number
  /** Länge in Metern, an der Strecke gemessen. */
  lengthM: number
  /** Name des Bauwerks, so wie er auf dem Schild steht. Oft leer. */
  name?: string
  /** Nummer der Strasse, etwa "A11". */
  ref?: string
}

/**
 * Was ausser den Abbiegeanweisungen noch über die Strecke bekannt ist.
 *
 * Die beiden Angaben kommen aus verschiedenen Quellen und stehen trotzdem
 * zusammen: für den Vorschlag sind sie dasselbe - Stellen der Strecke, die in
 * keinem Manöver auftauchen und die niemand von Hand eintragen kann, weil er
 * die Strasse vorher nicht fährt.
 *
 * Beide dürfen fehlen. Eine Strecke ohne Kehren und ohne Tunnel ist der
 * Normalfall, und eine Strecke ohne Kartendaten ist es auch.
 */
export interface RouteFeatures {
  /**
   * Die Kehrenfolgen, aus der vollen Geometrie gezählt (serpentineRuns in
   * lib/route.ts).
   */
  serpentines?: readonly SerpentineRun[]
  /** Die Tunnel aus dem Kachelarchiv des Servers. */
  tunnels?: readonly RouteTunnel[]
  /**
   * Die unbefestigten Abschnitte, in OpenStreetMap nachgeschlagen.
   *
   * Sie kommen aus der Etappe und nicht aus einem frischen Abruf: der Belag
   * wird auf eigenen Knopfdruck geholt (StageSurfacePanel) und liegt danach in
   * der Strecke - dieselbe Regel wie bei den Kehren. Ein Vorschlag, der ihn
   * nebenbei mitholte, hinge an einem Dienst, der Sekunden bis Minuten braucht.
   */
  surface?: readonly SurfaceSection[]
}

/**
 * Ein Vorschlag: eine fertige Tulpe und das Gewicht, mit dem sie unter den
 * Deckel kommt.
 *
 * Eine Tulpe und kein eigener Typ daneben, weil der Nutzer genau das bekommt,
 * was er sieht: Zeichen, Kilometer und Hinweis, ohne Umrechnung dazwischen.
 */
export interface TulipSuggestion extends Tulip {
  /** Wie schwer der Vorschlag wiegt, wenn mehr da sind als der Deckel zulässt. */
  weight: number
}

/**
 * Bis hierher gilt ein Arm als geradeaus, in Grad.
 *
 * Dreissig, weil die Notation auf dem Achtelraster liegt: was weniger als eine
 * halbe Rasterstufe von der Fahrtrichtung abweicht, ist im Auto keine
 * Abbiegung, sondern eine Kurve. Dieselbe Zahl entscheidet zweierlei - ob eine
 * Kreuzung einen geradeaus führenden Arm hat, und ob das Manöver selbst nur
 * ein Weiterfahren ist.
 */
const GERADE = 30

/**
 * Wie weit der Pfeil höchstens neben dem wirklichen Winkel liegen darf.
 *
 * Die Formen kennen feste Richtungen (siehe TULIP_SHAPES). Liegt keine davon
 * näher als fünfunddreissig Grad am gemessenen Winkel, zeigte der Pfeil in
 * einen Arm, den es an dieser Kreuzung nicht gibt - dann fällt der Vorschlag
 * weg, statt eine falsche Richtung zu behaupten.
 *
 * Diese Zahl gilt für die Formen, deren Pfeil einen *Winkel* behauptet -
 * Kreisverkehr, Kreuzung, Abzweig. Die Formen aus NACH_SEITE behaupten keinen
 * Winkel, sondern eine Seite, und sie werden deshalb nicht hieran gemessen,
 * sondern an seite(). Das ist kein Schlupfloch, sondern eine zweite Prüfung
 * mit demselben Versprechen: sagt keine Quelle die Seite oder widersprechen
 * sich die Quellen, gibt es keinen Vorschlag statt eines falschen Zeichens.
 *
 * Was diese Zahl allein *nicht* leistet, ist die Kehrtwende - sie fällt bei den
 * Winkelformen zwar nebenbei mit weg, weil keine ihrer Richtungen so weit
 * hinten liegt, aber die Formen aus NACH_SEITE rasten nicht ein. Dafür steht
 * WENDE.
 */
const MAX_ABWEICHUNG = 35

/**
 * Ab diesem Winkel ist das Manöver eine Kehrtwende und keine Abbiegung, in Grad.
 *
 * Der Gegenwinkel von MAX_ABWEICHUNG, und deshalb von dort abgeleitet: was
 * weniger als fünfunddreissig Grad neben der Gegenrichtung liegt, führt zurück,
 * woher man kam. Eine Kehrtwende ist an keiner Form des Katalogs vorgesehen -
 * bei den Winkelformen folgt das schon aus MAX_ABWEICHUNG, denn keine ihrer
 * Richtungen liegt so weit hinten, dass dort noch etwas einrastete.
 *
 * Bei den Formen aus NACH_SEITE folgt es aus nichts, und darum steht die Zahl
 * hier: seite() liest ein Vorzeichen, und das Vorzeichen einer Kehrtwende ist
 * Rauschen. Ob der Dienst 176 oder 184 Grad meldet, ist derselbe Bogen um die
 * Mittelinsel; als Vorzeichen gelesen sind es links und rechts. Im Buch stünde
 * dann ein T-Abbieger mit einer ausgewürfelten Seite, wo in Wirklichkeit zu
 * wenden ist.
 */
const WENDE = 180 - MAX_ABWEICHUNG

/**
 * Bis zu diesem Winkel sagt die Krümmung nichts über die Seite, in Grad.
 *
 * Drei Grad sind auf hundert Metern fünf Meter Versatz - das ist der Bogen
 * einer Fahrbahn und keine Abzweigung. Darunter entscheidet allein der
 * Modifikator des Dienstes.
 */
const FLACH = 3

/**
 * Abstand, unter dem zwei Vorschläge dieselbe Stelle meinen, in Metern.
 *
 * OSRM zerlegt eine Kreuzung gern in zwei Manöver - erst der Schwenk auf die
 * Abbiegespur, dann das Abbiegen. Hundertfünfzig Meter sind auf Landstrassen
 * weniger als eine Kreuzung breit ist und im Ortskern immer noch kürzer als
 * der Abstand zweier Ecken.
 */
const NAHE_M = 150

/**
 * Wie weit Rampe und Gabelung eines Autobahnknotens auseinanderliegen dürfen,
 * um als eine Stelle zu gelten, in Metern.
 *
 * Ein Autobahnknoten ist in den Daten eine Folge aus Ausfahrt, Gabelung und
 * Einfädeln, die sich über einen Kilometer zieht. Im Roadbook ist das eine
 * Zeile: "raus Richtung Salzburg". Drei Zeilen dafür wären drei Zeichen für
 * einen einzigen Entschluss.
 */
const KNOTEN_M = 1000

/**
 * Wie viele Vorschläge eine Etappe höchstens bekommt.
 *
 * Zwanzig, und die Zahl ist nicht gegriffen: so viele Tulpen passen auf die
 * Etappenseite und eine einzige Roadbookseite dahinter (MAX_TULPEN_ERSTE_SEITE
 * plus ROADBOOK_TULPEN in features/print/stagePlan.ts, 4 + 16). Der Deckel
 * heisst also: ein Vorschlag darf das Buch um höchstens eine Seite je Etappe
 * dicker machen. Er steht hier noch einmal und wird nicht von dort geholt,
 * weil dieses Modul nichts vom Druck wissen soll - ändert sich die Seite,
 * ändert sich hier eine Zahl, und nicht die Abhängigkeit.
 *
 * Greift der Deckel, bleibt das Schwerere stehen; was gleich schwer ist, bleibt
 * in Fahrreihenfolge. Siehe gewicht().
 */
export const MAX_VORSCHLAEGE = 20

/**
 * Wie lang ein erzeugter Hinweis höchstens wird.
 *
 * Im Buch steht er rechts neben einem Zeichen von 19 mm, in einer Spalte von
 * zwei. Vierzig Zeichen sind dort gut zwei Zeilen; was länger ist, drängt die
 * Zeile auseinander oder wird weggeschnitten. Von Hand darf jeder schreiben,
 * was er will - dieser Deckel gilt nur für das, was die Anwendung selbst
 * vorschlägt.
 */
export const NOTIZ_MAX = 40

/** Manöver, an denen nichts zu entscheiden ist. */
const OHNE_WAHL = new Set([
  // Anfang und Ende einer Teilstrecke. Bei einer Etappe mit Zwischenpunkten
  // stehen sie auch mitten drin - am Vršič etwa -, und dort sind sie erst recht
  // keine Anweisung: die Strasse läuft weiter, nur die Rechnung des Dienstes
  // hat dort eine Naht.
  'depart',
  'arrive',
  // Die Strasse wechselt den Namen. Genau das, was niemand im Roadbook braucht.
  'new name',
  // Hinweise ohne Handlung.
  'notification',
  'use lane',
  // Die zweite Hälfte eines Kreisverkehrs. Sie gehört zur ersten und wird dort
  // mitgelesen - zwei Zeichen für einen Kreisel wären zwei Kreisel.
  'exit roundabout',
  'exit rotary',
])

/**
 * Der Modus eines Schritts, der über Wasser geht.
 *
 * OSRM setzt ihn aus dem Profil: alles, was in den Kartendaten route=ferry
 * ist, bekommt diesen Wert. Er ist damit die einzige Stelle, an der eine
 * Überfahrt in der Antwort überhaupt steht.
 */
const FAEHRE = 'ferry'

/** Manöver, die einen Kreisverkehr beginnen. */
const KREISEL = new Set(['roundabout', 'rotary', 'roundabout turn'])

/** Manöver an einer gewöhnlichen Kreuzung: hier hängt die Form an den Armen. */
const WAHL = new Set(['turn', 'end of road', 'continue'])

/**
 * Formen, bei denen ein kleiner Winkel trotzdem eine Entscheidung ist.
 *
 * Eine Gabelung teilt sich um wenige Grad, und eine Autobahnrampe zweigt fast
 * parallel ab - wer diese Manöver an ihrem Winkel misst, wirft genau die
 * Stellen weg, an denen man sich verfährt. Bei ihnen sagt das Vorzeichen die
 * Seite, und mehr sagt die Form ohnehin nicht: Gabelung links oder rechts,
 * Rampe links oder rechts. Dasselbe an der T-Kreuzung: geradeaus geht dort
 * nicht, es bleiben zwei Arme, und der Pfeil auf 90 oder 270 ist ihr Bild und
 * nicht ihr Winkel - die beiden Äste des Trenta-Abzweigs liegen bei ±60 Grad.
 *
 * Weil hier keine Winkelprüfung greifen kann, gibt es für diese vier Formen
 * eine eigene: seite(). Sie fragt beide Quellen - die Krümmung und den
 * Modifikator des Dienstes - und liefert nichts, wenn beide schweigen oder
 * einander widersprechen.
 */
const NACH_SEITE = new Set<TulipShape>(['gabel', 'auffahrt', 'abfahrt', 't'])

/** Die Richtungen, die eine Form kennt. Dieselben Werte wie in TULIP_SHAPES. */
const RICHTUNGEN: Partial<Record<TulipShape, number[]>> = {
  kreisel: [270, 0, 90],
  kreuzung: [270, 0, 90],
  t: [270, 90],
  gabel: [315, 45],
  abzweig: [270, 315, 45, 90],
  auffahrt: [90, 270],
  abfahrt: [90, 270],
}

/** Ein Vorschlag, solange er noch aus Rohdaten besteht. */
interface Kandidat {
  /** Kilometerstand ab Beginn der Etappe. */
  km: number
  shape: TulipShape
  turn: number
  exit: number
  name: string
  ref: string
  dest: string
  /**
   * Womit die Strasse vor diesem Manöver angeschrieben war.
   *
   * Die erste Nummer des Schildes und nicht das ganze, weil gewicht() sie mit
   * kennung() vergleicht und damit fragt, ob die Strasse ihre Nummer wechselt.
   * Ob sich hier zwei Strassen trennen, ist eine andere Frage - die steht in
   * sammeln und wird dort auf dem ganzen Schild beantwortet.
   */
  vorher: string
  /**
   * Ist dieser Vorschlag die Rampe eines Autobahnknotens?
   *
   * Gesetzt, wenn ein Einfädeln ihn abgeschlossen hat (siehe knotenVor). Bei
   * einer Gabelung ist das die einzige Auskunft darüber, dass sie keine
   * gewöhnliche Weggabelung ist, sondern die Stelle, an der man auf die
   * Autobahn geht - die Art des Manövers sagt dort nur "fork". Für das Gewicht
   * ist das der Unterschied zwischen zwei und vier: eine verpasste Auffahrt
   * kostet zwanzig Kilometer, ganz gleich, wie OSRM das Manöver nennt.
   */
  knoten?: boolean
  /**
   * Fertiger Hinweis samt Gewicht, gesetzt bei allem, was keine Abbiegung ist.
   *
   * Kehren, Tunnel und die Fähre bringen ihren Text mit, statt ihn aus Name,
   * Nummer und Wegweiser zusammensetzen zu lassen: an ihnen biegt niemand ab,
   * ein Wegweiser steht dort nicht. Was sie sagen, ist ein Name und eine
   * Länge, und das ist eine andere Art von Satz.
   */
  fertig?: { note: string; weight: number }
}

/**
 * Die Vorschläge zu einer Strecke, in Fahrreihenfolge.
 *
 * @param steps Die Manöver der Strecke, wie der Server sie liefert - über alle
 *   Teilstrecken hinweg aneinandergehängt.
 * @param features Was ausserdem über die Strecke bekannt ist: Kehren und
 *   Tunnel. Beides steht in keiner Abbiegeanweisung, und beides ist genau das,
 *   was von Hand niemand einträgt - man kennt es erst, wenn man die Strasse
 *   gefahren ist.
 * @param max Deckel je Etappe.
 * @returns Fertige Tulpen samt Gewicht. Wird nichts gefunden, ist die Liste
 *   leer; das ist bei einer reinen Autobahnetappe die richtige Antwort und kein
 *   Fehler.
 */
export function tulipSuggestions(
  steps: readonly RouteStep[],
  features: RouteFeatures = {},
  max = MAX_VORSCHLAEGE,
): TulipSuggestion[] {
  const kandidaten = sammeln(steps)
  const gelichtet = zusammenfassen(kandidaten)
  // Die Streckenstellen kommen nach dem Zusammenfassen dazu und laufen nicht
  // durch es hindurch. Der Grund ist der Abstand von NAHE_M: eine Abbiegung
  // hundert Meter vor dem Tunnelmund ist nicht dieselbe Stelle wie der Tunnel,
  // und wer beides zusammenlegt, verliert das Zeichen, das er nicht ersetzen
  // kann - eine Kreuzung kann man sehen, einen Tunnel erst, wenn man drin ist.
  const alle = einordnen(gelichtet, streckenstellen(features))
  const gedeckelt = deckeln(alle, max)

  return gedeckelt.map((k) => ({
    km: k.km,
    shape: k.shape,
    turn: k.turn,
    note: notiz(k),
    weight: gewicht(k),
  }))
}

/**
 * Macht aus Kehren, Tunneln und Belag Kandidaten.
 *
 * Beide bekommen ihr Gewicht hier und nicht in gewicht(): dort steht die
 * Rangfolge der Manöver, und die misst den Schaden eines verpassten Hinweises
 * daran, wie weit man dann falsch fährt. Für diese beiden gilt das nicht - an
 * einer Kehre verfährt sich niemand. Sie wiegen trotzdem schwer, und zwar aus
 * dem umgekehrten Grund: sie sind die einzigen Zeilen, die überhaupt nur so ins
 * Buch kommen. Alles andere könnte ein geduldiger Mensch aus einer Karte
 * abschreiben.
 *
 * Die Kehren stehen weit oben, gleichauf mit den Autobahnrampen. „24 Kehren
 * hoch, 2. Gang, Kuehlwasser“ ist die Zeile, für die diese Notation gebaut
 * wurde; sie unter zwanzig Kreiseln zu verlieren wäre die schlechteste
 * mögliche Auswahl. Schwerer wiegen nur die Fähre - die aus den Manövern kommt,
 * siehe sammeln - und der schwierige Belag, siehe belagGewicht.
 */
function streckenstellen(features: RouteFeatures): Kandidat[] {
  const out: Kandidat[] = []

  for (const folge of features.serpentines ?? []) {
    if (!Number.isFinite(folge.km) || !Number.isFinite(folge.count) || folge.count < 1) continue
    out.push({
      ...leererKandidat(folge.km, 'serpentine'),
      fertig: { note: kehrenNotiz(folge), weight: 4 },
    })
  }

  for (const tunnel of features.tunnels ?? []) {
    if (!Number.isFinite(tunnel.km) || !Number.isFinite(tunnel.lengthM)) continue
    out.push({
      ...leererKandidat(tunnel.km, 'tunnel'),
      fertig: { note: tunnelNotiz(tunnel), weight: 3 },
    })
  }

  for (const abschnitt of features.surface ?? []) {
    if (!Number.isFinite(abschnitt.fromKm) || !Number.isFinite(abschnitt.lengthM)) continue
    out.push({
      ...leererKandidat(abschnitt.fromKm, 'schotter'),
      fertig: { note: belagNotiz(abschnitt), weight: belagGewicht(abschnitt) },
    })
  }

  return out.sort((a, b) => a.km - b.km)
}

/**
 * Wie schwer eine Belagszeile wiegt.
 *
 * Fünf für alles ab „grob“, gleichauf mit der Fähre und damit an der Spitze.
 * Das ist keine Übertreibung: eine verpasste Fähre kostet Stunden, eine
 * übersehene Geländepiste kostet den Abend und im schlechten Fall die Achse.
 * Beides sind Zeilen, bei denen man vorher umdisponiert und nicht unterwegs
 * reagiert.
 *
 * Vier für „locker“, gleichauf mit den Kehren. Eine geschotterte Nebenstrasse
 * ist keine Umplanung, aber sie ändert das Tempo und damit die Ankunftszeit -
 * und sie steht in keiner anderen Quelle.
 */
function belagGewicht(abschnitt: SurfaceSection): number {
  return abschnitt.grade === 'locker' ? 4 : 5
}

/** Ein Kandidat ohne Knotenangaben - für die Formen, die keine kennen. */
function leererKandidat(km: number, shape: TulipShape): Kandidat {
  // turn bleibt 0: Tunnel und Kehren kennen keine Richtung (siehe directions in
  // TULIP_SHAPES), und tulipArms zeichnet für sie keine Arme. Eine Zahl, die
  // niemand liest, soll trotzdem nicht geraten aussehen.
  return { km, shape, turn: 0, exit: 0, name: '', ref: '', dest: '', vorher: '' }
}

/**
 * Der Hinweis einer Kehrenfolge: wie viele, wie lang.
 *
 * Ohne "hinauf" oder "hinunter". Die Höhen stehen in keiner dieser beiden
 * Quellen, und die Reihenfolge im Streckenverlauf sagt es nicht - am Vršič
 * folgen Auffahrt und Abfahrt aufeinander, und welche zuerst kommt, hängt
 * daran, von welcher Seite man kommt. Eine geratene Richtung stünde neben einer
 * gezählten Zahl und sähe genauso verlässlich aus. Wer den Gang dazuschreibt,
 * weiss ohnehin, wohin es geht.
 */
function kehrenNotiz(folge: SerpentineRun): string {
  const anzahl = Math.round(folge.count)
  const laenge = Number.isFinite(folge.lengthKm) ? folge.lengthKm : 0
  const kopf = anzahl === 1 ? '1 Kehre' : `${String(anzahl)} Kehren`
  return laenge >= 0.1 ? `${kopf} auf ${laenge.toFixed(1)} km` : kopf
}

/**
 * Der Hinweis eines Tunnels: Name, sonst nur das Wort, dazu die Länge.
 *
 * Der Name bleibt, wie er auf dem Schild steht - „Predor Karavanke“ wird nicht
 * eingedeutscht, sonst findet man ihn dort nicht wieder. Dieselbe Regel wie bei
 * den Strassennamen.
 */
function tunnelNotiz(tunnel: RouteTunnel): string {
  const name = (tunnel.name ?? '').trim()
  return kuerzen(`${name || 'Tunnel'}, ${laengeText(tunnel.lengthM)}`)
}

/**
 * Eine Länge, wie sie im Hinweis steht.
 *
 * Unter einem Kilometer in Metern: „850 m“ liest sich im Auto schneller als
 * „0.9 km“, und darüber ist die Nachkommastelle die genauere Auskunft.
 */
function laengeText(meter: number): string {
  const m = Math.max(0, Math.round(meter))
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${String(m)} m`
}

/** Kürzt einen Hinweis sichtbar auf den Deckel. */
function kuerzen(text: string): string {
  return text.length > NOTIZ_MAX ? `${text.slice(0, NOTIZ_MAX - 1).trimEnd()}…` : text
}

/**
 * Der Hinweis einer Fähre: wie sie heisst und wie weit sie fährt.
 *
 * Der Name kommt vom Dienst und steht so in der Karte - „Prizna - Žigljen
 * (Pag)“, „Barcelona – Alcúdia“, „Lavik - Oppedal“. Er bleibt unverändert,
 * dieselbe Regel wie bei Strassen- und Tunnelnamen: am Hafen steht er so
 * angeschrieben.
 *
 * Was hier ausdrücklich *nicht* steht, ist die Abfahrtszeit - und die ist das
 * Wichtigste an einer Fähre. Der Dienst kennt keinen Fahrplan, und eine
 * geratene Zeit neben einem gemessenen Namen sähe genauso verlässlich aus. Die
 * Zeile ist deshalb kurz gehalten, damit die Zeit von Hand danebenpasst; die
 * Rechnung dazu steht bei TULPEN_NOTIZ_ZEICHEN_MAX in lib/tulip.ts.
 *
 * Auch nicht: die Fahrzeit, obwohl der Dienst eine Dauer liefert. Sie stammt
 * bei Fähren mit `duration` in den Kartendaten aus dem Fahrplan und ist dann
 * richtig - fehlt das Merkmal, rechnet OSRM mit 5 km/h weiter, und aus zwanzig
 * Minuten würden sechsunddreissig. Eine Zahl, die in der einen Hälfte der Fälle
 * stimmt und in der anderen um das Doppelte danebenliegt, ohne dass man ihr das
 * ansieht, gehört nicht ins Buch.
 */
function faehreNotiz(step: RouteStep): string {
  const name = (step.name ?? '').trim()
  const laenge = laengeText(Number.isFinite(step.distance) ? step.distance : 0)
  return kuerzen(name ? `${name}, ${laenge}` : `Fähre, ${laenge}`)
}

/**
 * Fügt die Streckenstellen zwischen die Manöver ein, nach Kilometerstand.
 *
 * Beide Listen sind schon aufsteigend, es bleibt also ein Reissverschluss. Bei
 * gleichem Kilometerstand steht die Streckenstelle hinter dem Manöver: wer an
 * einer Kreuzung abbiegt und dann in den Tunnel fährt, liest es in dieser
 * Reihenfolge.
 */
function einordnen(manoever: readonly Kandidat[], stellen: readonly Kandidat[]): Kandidat[] {
  const out: Kandidat[] = []
  let i = 0
  let j = 0
  while (i < manoever.length || j < stellen.length) {
    const links = manoever[i]
    const rechts = stellen[j]
    if (links && (!rechts || links.km <= rechts.km)) {
      out.push(links)
      i++
      continue
    }
    if (rechts) {
      out.push(rechts)
      j++
    }
  }
  return out
}

/**
 * Fügt angenommene Vorschläge in die Tulpen einer Etappe ein.
 *
 * Vorhandene Tulpen verschwinden nicht und werden nicht verändert - wer schon
 * welche eingetragen hat, bekommt Vorschläge dazu und nicht statt. Das ist der
 * Grund, warum diese Funktion existiert und nicht einfach angehängt wird.
 *
 * Einsortiert wird nach Kilometerstand: ein Vorschlag steht vor der ersten
 * vorhandenen Tulpe, die weiter hinten liegt. Tulpen ohne Kilometerstand
 * bleiben, wo sie sind - sie sind unterwegs notiert worden, ihre Stelle in der
 * Liste ist die einzige Auskunft über ihren Platz, und eine Sortierung, die sie
 * verschiebt, machte aus einer Reihenfolge eine Vermutung. Ein Vorschlag landet
 * deshalb hinter ihnen, wenn sich nichts Späteres findet.
 *
 * Was schon dasteht, kommt nicht ein zweites Mal dazu. Zusammengelegt wird auch
 * innerhalb eines Laufes (zusammenfassen), aber das hilft hier nichts: dort
 * werden die Kandidaten *einer* Antwort gelichtet, hier trifft die zweite
 * Antwort auf das, was von der ersten schon im Buch steht. Wer zweimal
 * übernimmt, bekommt deshalb keine zweite gleiche Zeile - und die vorhandene
 * bleibt, wie sie ist, samt einem von Hand berichtigten Kilometerstand. Woran
 * "dieselbe Stelle" erkannt wird, steht in lib/tulipMerge.ts.
 *
 * @param vorhanden Die Tulpen der Etappe, in Fahrreihenfolge.
 * @param neue Die angenommenen Vorschläge, aufsteigend nach Kilometer.
 */
export function tulipsEinfuegen(vorhanden: readonly Tulip[], neue: readonly Tulip[]): Tulip[] {
  const out = [...vorhanden]
  let ab = 0

  const schonDa = stellenZuordnung(neue.map(tulipStelle), vorhanden.map(tulipStelle))

  for (const [i, tulpe] of neue.entries()) {
    if ((schonDa[i] ?? -1) >= 0) continue

    let platz = out.length
    for (let j = ab; j < out.length; j++) {
      const km = out[j]!.km
      if (Number.isFinite(km) && km > tulpe.km) {
        platz = j
        break
      }
    }
    out.splice(platz, 0, tulpe)
    // Der nächste Vorschlag liegt weiter hinten, gesucht wird also erst dahinter.
    // Ohne das landeten zwei Vorschläge zwischen denselben zwei vorhandenen
    // Tulpen in umgekehrter Reihenfolge.
    ab = platz + 1
  }
  return out
}

/**
 * Der Kilometerstand einer Tulpe ist die Summe der Abschnitte davor.
 *
 * Der Dienst zählt ab dem Beginn der Route, und die Route einer Etappe beginnt
 * an ihrem Startpunkt - das ist genau der Kilometerbegriff der Tulpe ("ab
 * Beginn der Etappe", siehe Tulip in types.ts). Die Zahlen passen also
 * zusammen, und zwar auch über Zwischenpunkte hinweg: die Teilstrecken werden
 * aneinandergehängt, ihre Nahtstellen zählen weiter und fangen nicht neu an.
 *
 * Was sie nicht ist: der Tachostand. Sie stimmt mit ihm überein, solange
 * gefahren wird, was gerechnet wurde - und wo das nicht mehr stimmt, ist der
 * Wert im Feld ohnehin von Hand zu ändern. Eine Nachkommastelle, weil im Buch
 * auch nur eine steht (tulipKm).
 */
function kilometer(meter: number): number {
  return Math.round(meter) / 1000
}

/** Bringt einen Winkel auf -180 bis 180. */
function versatz(grad: number): number {
  const v = ((grad % 360) + 360) % 360
  return v > 180 ? v - 360 : v
}

/**
 * Die Arme des Knotens, gemessen von der Fahrtrichtung aus.
 *
 * Der Arm, aus dem man kommt, liegt bei 180 - unten im Zeichen, wo der Punkt
 * sitzt. Der Dienst nennt die Winkel als Himmelsrichtungen und die Stelle des
 * Arms, aus dem man kommt; dessen Winkel zeigt zurück, die Fahrtrichtung ist
 * also sein Gegenwinkel.
 *
 * @returns null, wenn der Knoten nicht zu deuten ist. Dann fällt der Vorschlag
 *   weg - eine Kreuzung, deren Zufahrt niemand kennt, liesse sich nur geraten
 *   zeichnen, und ein gespiegeltes Zeichen macht aus rechts links.
 */
function arme(step: RouteStep): number[] | null {
  const bearings = step.bearings
  const ein = step.in ?? -1
  if (!bearings || ein < 0 || ein >= bearings.length) return null

  const fahrt = bearings[ein]! + 180
  return bearings.map((b) => versatz(b - fahrt))
}

/**
 * Wie viele befahrbare Strassen es an diesem Knoten ausser der gefahrenen gibt.
 *
 * Das ist die schärfste Regel dieses Moduls und die einzige, die rein aus der
 * Geometrie kommt: gibt es hier nichts, wohin man sonst fahren dürfte, kann man
 * sich hier nicht verfahren - und eine Tulpe an einer Stelle ohne Wahl ist eine
 * Zeile, die niemand liest. Die entry-Flags stammen aus Einbahnstrassen und
 * Abbiegeverboten; ein Weg, der nur so aussieht, als könnte man ihn nehmen,
 * zählt damit richtigerweise nicht mit.
 *
 * @returns -1, wenn der Knoten keine Auskunft gibt. Dann wird nichts
 *   weggeworfen: die Regel darf nur streichen, was sie kennt.
 */
function auswege(step: RouteStep): number {
  const { bearings, entry } = step
  const ein = step.in ?? -1
  const aus = step.out ?? -1
  if (!bearings || !entry || entry.length !== bearings.length || ein < 0 || aus < 0) return -1

  let n = 0
  for (let i = 0; i < entry.length; i++) {
    if (i !== ein && i !== aus && entry[i]) n++
  }
  return n
}

/**
 * Alles, womit die Strasse angeschrieben ist - in der Reihenfolge des Schildes.
 *
 * Der Dienst trennt mehrere Nummern mit Semikolon: "A2; A3" ist ein Stück
 * Autobahn, das zwei Nummern trägt, weil dort zwei Autobahnen dieselbe Fahrbahn
 * benutzen. Das ist keine Schreibweise, sondern eine Auskunft - eine Strasse
 * mit zwei Nummern sind zwei Strassen auf einem gemeinsamen Stück, und wo sie
 * sich trennen, muss man sich für eine entscheiden (siehe sammeln).
 *
 * Fehlt die Nummer, steht der Name an ihrer Stelle. Ein Name ist immer nur
 * einer - zwei Strassen teilen sich eine Fahrbahn, aber keinen Namen.
 */
function kennungen(step: { ref?: string; name?: string }): string[] {
  const nummern = (step.ref ?? '')
    .split(';')
    .map((n) => n.trim())
    .filter((n) => n !== '')
  if (nummern.length > 0) return nummern

  const name = (step.name ?? '').trim()
  return name ? [name] : []
}

/**
 * Womit die Strasse angeschrieben ist: Nummer vor Name, sonst nichts.
 *
 * Die erste Nummer des Schildes und nicht alle - überall dort, wo eine Strasse
 * *benannt* wird, im Hinweis und im Gewicht, ist eine Nummer die Auskunft, die
 * man im Auto sucht. Wo es dagegen um die Frage geht, ob sich hier etwas
 * trennt, ist das ganze Schild gemeint; dafür steht kennungen().
 */
function kennung(step: { ref?: string; name?: string }): string {
  return kennungen(step)[0] ?? ''
}

/**
 * Fährt man hinter dem Manöver noch auf allem, worauf man davor fuhr?
 *
 * Die Frage hinter dem Vergleich ist nicht "heisst die Strasse noch gleich",
 * sondern "ist hier eine weggefallen". Verliert das Schild eine Nummer, haben
 * sich zwei Strassen getrennt, und das ist genau die Stelle, an der man sich
 * entscheiden muss. Kommt eine dazu, sind zwei zusammengelaufen - dort ist
 * nichts zu entscheiden, man bleibt auf der Fahrbahn.
 *
 * Ohne ein Schild davor gibt es nichts zu vergleichen, und dann ist die Antwort
 * nein: die erste angeschriebene Strasse einer Etappe ist nie ein "weiter wie
 * bisher".
 */
function dieselbeStrasse(neu: readonly string[], vorher: readonly string[]): boolean {
  const neueSchreibweisen = neu.map(eng)
  return vorher.length > 0 && vorher.every((kennung) => neueSchreibweisen.includes(eng(kennung)))
}

/**
 * Welche Form dieses Manöver bekommt - oder keine.
 *
 * Die Zuordnung ist eng und lässt lieber etwas weg, als eine Form zu wählen,
 * die etwas anderes behauptet als das, was dort steht: ein Zeichen mit drei
 * Armen an einer Kreuzung mit fünfen wäre eine Kreuzung, die es nicht gibt.
 * Deshalb hat auch nicht jedes Manöver eine Form - Grenzen, Mautstellen,
 * Bahnübergänge und Ortsdurchfahrten stehen in keiner Abbiegeanweisung und
 * bleiben Handarbeit. Kehren, Tunnel und Belag kommen aus anderen Quellen
 * (siehe RouteFeatures), die Fähre aus dem Modus des Schritts und nicht aus der
 * Art des Manövers (siehe sammeln).
 *
 * Der Schotter stand hier lange als der Fall, bei dem es beim Suchen bleibt,
 * mit einer Begründung, die in ihren beiden Feststellungen richtig war und im
 * Schluss falsch: OpenStreetMap führt surface=unpaved, der Routendienst reicht
 * es nicht durch, und die eigene Basiskarte führt in ihrer Strassenebene zwar
 * is_tunnel und die Strassenklasse, aber keinen Belag. Beides stimmt. Nur
 * folgt daraus nicht, dass nichts zu holen wäre.
 *
 * Der Routendienst reicht auf Anfrage die OSM-Knotenkennungen jedes befahrenen
 * Wegstücks durch, und damit lässt sich der Weg in der OSM-Datenbank
 * nachschlagen - dort steht der Belag. Der Server tut das
 * (backend/internal/surface) und liefert Kilometerbereiche; hier werden daraus
 * Tulpen, siehe streckenstellen. Der Schotter ist also keine Handarbeit mehr.
 */
function formVon(step: RouteStep): TulipShape | null {
  const typ = step.type
  // Ein Kreisverkehr ist am Zeichen zu erkennen und nicht am Winkel: der Ring
  // mit der Insel ist der Unterschied, der entscheidet, ob man anhält oder sich
  // einfädelt. "roundabout turn" ist ein Minikreisel - für den Fahrer immer
  // noch ein Ring mit einer Insel darin.
  if (KREISEL.has(typ)) return 'kreisel'
  if (typ === 'fork') return 'gabel'
  // "on ramp" ist der Anfang einer Auffahrt - die Stelle, an der man sie nimmt.
  //
  // Ihr Ende, "merge", steht hier ausdrücklich nicht. Siehe sammeln: das
  // Einfädeln bekommt kein eigenes Zeichen.
  if (typ === 'on ramp') return 'auffahrt'
  if (typ === 'off ramp') return 'abfahrt'
  if (!WAHL.has(typ)) return null

  const winkel = arme(step)
  if (!winkel) return null

  // Der Arm, aus dem man kommt, zählt nicht mit: er ist der Punkt unten.
  const andere = winkel.filter((w) => Math.abs(Math.abs(w) - 180) > GERADE)
  if (winkel.length === 3) {
    // Drei Arme: entweder läuft einer geradeaus weiter - dann ist es ein
    // Abzweig, die Hauptstrasse geht durch und ein Weg geht ab -, oder keiner,
    // und dann endet die Strasse an einer Querstrasse.
    return andere.some((w) => Math.abs(w) <= GERADE) ? 'abzweig' : 't'
  }
  if (winkel.length === 4) return 'kreuzung'
  // Zwei Arme sind keine Kreuzung, fünf sind keine Form des Katalogs.
  return null
}

/**
 * Auf welche Seite es geht - oder auf keine, die zu belegen wäre.
 *
 * Für die Formen aus NACH_SEITE ist das die ganze Auskunft des Zeichens, und
 * sie hat zwei Quellen, die einander bestätigen müssen:
 *
 *  - die Krümmung, also der Unterschied zwischen Fahrtrichtung davor und
 *    danach. Sie ist die genauere, solange sie deutlich genug (FLACH) und keine
 *    Kehrtwende ist (WENDE);
 *  - der Modifikator des Dienstes, also das, was OSRM selbst über die Seite
 *    sagt. Er ist die einzige Quelle, wenn die Strasse gerade durchläuft.
 *
 * Widersprechen sie sich, gibt es keinen Vorschlag. Das ist keine Vorsicht auf
 * Vorrat, sondern die Lehre aus dem Einfädeln: an keinem der sechzehn
 * `merge`-Knoten der echten Antwortdateien bestätigt die Krümmung den
 * Modifikator - zwölfmal sagt OSRM „slight left“ und die Krümmung „rechts“,
 * viermal ist die Krümmung zu flach, um überhaupt etwas zu sagen. Das liegt
 * daran, dass die Krümmung dort die Ausrichtung auf der Rampe beschreibt
 * und der Modifikator die Fahrbahn, in die man wechselt. Vor dieser Prüfung
 * stand deshalb im Buch „Autobahnauffahrt rechts“, wo die Autobahn links lag -
 * und alle Tests waren grün. Zwei Quellen, die einander widersprechen, sind
 * keine Auskunft; keine Zeile ist besser als die falsche Seite.
 *
 * @returns true für links, false für rechts, null für „nicht zu belegen“.
 */
function seite(step: RouteStep, roh: number): boolean | null {
  // Eine Kehrtwende hat keine Seite, die eine dieser vier Formen zeigen könnte.
  // Das gilt auch dann, wenn der Dienst eine nennt: gemeint wäre die Seite, an
  // der man wendet, und gezeichnet stünde ein Abbieger, dem man geradeaus
  // folgen würde. Deshalb steht die Prüfung vor beiden Quellen und nicht
  // zwischen ihnen.
  if (Math.abs(roh) >= WENDE) return null

  const mod = step.modifier ?? ''
  // "straight", "uturn" und ein fehlender Modifikator sagen nichts über die
  // Seite - und das ist etwas anderes als "rechts".
  const vomDienst = mod.includes('left') ? true : mod.includes('right') ? false : null
  const vomWinkel = Math.abs(roh) > FLACH ? roh < 0 : null

  if (vomDienst !== null && vomWinkel !== null && vomDienst !== vomWinkel) return null
  return vomWinkel ?? vomDienst
}

/**
 * Auf welche Richtung der Form der gemessene Winkel fällt.
 *
 * Gerechnet wird auf dem rohen Winkel und nicht auf dem Achtelraster: normalize
 * Turn rundet erst auf 45 Grad, und eine Abbiegung um 63 Grad läge danach
 * genau zwischen "geradeaus" und "links" - an einer Kreuzung, die kein
 * Diagonalzeichen hat, entschiede dann der Losentscheid der Tie-Break-Regel.
 * Vom rohen Winkel aus ist es eindeutig links.
 *
 * @returns Richtung und Abweichung. Ist die Abweichung zu gross, hat der
 *   Aufrufer keine Form, die diesen Winkel zeigen kann.
 */
function einrasten(shape: TulipShape, roh: number): { turn: number; ab: number } {
  const moeglich = RICHTUNGEN[shape] ?? [0]
  let turn = moeglich[0]!
  let ab = Math.abs(versatz(turn - roh))
  for (const richtung of moeglich) {
    const d = Math.abs(versatz(richtung - roh))
    // Bei gleichem Abstand gewinnt rechts - dieselbe Regel wie in
    // exitDirection: die Seite, auf der man in Europa abbiegt, ohne den
    // Gegenverkehr zu kreuzen.
    if (d < ab || (d === ab && (richtung === 90 || richtung === 45))) {
      turn = richtung
      ab = d
    }
  }
  return { turn, ab }
}

/**
 * Sammelt die Kandidaten aus den Manövern, in Fahrreihenfolge.
 *
 * Der Kilometerstand läuft dabei mit, und ebenso das Schild der Strasse, auf
 * der man gerade fährt: ohne es liesse sich "geradeaus weiter auf derselben
 * Strasse" nicht von "geradeaus, aber ab hier heisst sie anders" unterscheiden,
 * und genau daran hängt die Hälfte der weggeworfenen Zeilen. Mitgeführt wird
 * das ganze Schild und nicht nur seine erste Nummer - eine Fahrbahn kann mehrere
 * tragen, und ob eine davon wegfällt, ist die Frage (siehe dieselbeStrasse).
 */
function sammeln(steps: readonly RouteStep[]): Kandidat[] {
  const out: Kandidat[] = []
  let meter = 0
  let vorher: string[] = []
  let vorigerModus = ''

  for (const [i, step] of steps.entries()) {
    const km = kilometer(meter)
    meter += Number.isFinite(step.distance) ? step.distance : 0
    const eigene = kennungen(step)
    const merken = () => {
      if (eigene.length > 0) vorher = eigene
    }
    const modus = step.mode ?? ''
    const warFaehre = vorigerModus === FAEHRE
    vorigerModus = modus

    if (modus === FAEHRE) {
      /*
       * Die Fähre wird vor allem anderen erkannt, und zwar am Modus.
       *
       * Vor allem anderen, weil die Art des Manövers sie sonst wegwürfe: an
       * Prizna - Žigljen steht dort "notification", und das ist ein Hinweis
       * ohne Handlung und damit in OHNE_WAHL. An Barcelona - Alcúdia steht
       * "turn", und dann liefe sie als Abbiegung durch und bekäme ein
       * Kreuzungszeichen. Beides an echten Antworten nachgesehen.
       *
       * Der Kilometerstand ist der des Anlegers und nicht der der Überfahrt:
       * die Zeile sagt, wo man vom Gas geht.
       *
       * Zerlegt der Dienst eine Überfahrt in mehrere Schritte, zählt nur der
       * erste. Zwei Zeichen für eine Fähre wären zwei Fähren, und die zweite
       * stünde mitten auf dem Wasser.
       */
      if (!warFaehre) {
        out.push({
          ...leererKandidat(km, 'faehre'),
          fertig: { note: faehreNotiz(step), weight: 5 },
        })
      }
      // vorher bleibt, wie es war: eine Fähre ist keine Strasse, auf der man
      // fährt. Ihre Nummer als "zuletzt befahren" zu merken hiesse, eine
      // Abbiegung auf eine gleich nummerierte Strasse dahinter zu verwerfen.
      continue
    }

    if (OHNE_WAHL.has(step.type)) {
      merken()
      continue
    }

    let roh = versatz(step.bearingAfter - step.bearingBefore)
    // Woher Name, Nummer und Wegweiser kommen. Meistens der Schritt selbst -
    // bei einem Kreisverkehr aber der Schritt, der ihn verlässt: dort steht die
    // Strasse, auf der es weitergeht.
    let quelle = step
    const naechster = steps[i + 1]

    if (KREISEL.has(step.type)) {
      // Die Drehung eines Kreisverkehrs ist der Winkel zwischen der Zufahrt und
      // der Ausfahrt, und die beiden stehen in zwei Schritten. Nur so entsteht
      // der Pfeil, den man im Auto sieht: die Modifikatoren des Dienstes
      // beschreiben stattdessen das Einfädeln in den Ring und das Verlassen -
      // beides folgt aus dem Rechtsverkehr und sagt nichts über die Richtung.
      if (naechster && (naechster.type === 'exit roundabout' || naechster.type === 'exit rotary')) {
        roh = versatz(naechster.bearingAfter - step.bearingBefore)
        quelle = naechster
      }
    } else if (step.type === 'merge') {
      /*
       * Das Einfädeln bekommt nie ein eigenes Zeichen.
       *
       * Drei Gründe, jeder für sich ausreichend:
       *
       * 1. Dort ist nichts zu entscheiden. Alle sechzehn `merge`-Knoten der
       *    Antwortdateien haben null befahrbare Alternativen - man kann nur
       *    weiterfahren. Genau das wirft auswege() sonst weg; die Regel griff
       *    hier bloss nicht, weil "merge" nicht in WAHL steht.
       * 2. Entschieden wurde schon vorher, an der Gabelung oder an der Rampe,
       *    und zwar oft einen halben Kilometer früher. Eine Zeile am Ende der
       *    Rampe steht hinter der Stelle, an der man handelt.
       * 3. Die Seite ist aus diesen Daten nicht zu gewinnen: Krümmung und
       *    Modifikator widersprechen einander systematisch (siehe seite()).
       *
       * Was das Einfädeln dagegen wirklich beiträgt, ist die Strasse, auf die
       * es geht - eine Rampe trägt oft weder Namen noch Nummer. Das wird dem
       * Knoten nachgetragen, zu dem es gehört.
       */
      const knoten = knotenVor(out, km)
      if (knoten) {
        ergaenzen(knoten, { ref: step.ref, name: step.name, dest: step.destinations })
        knoten.knoten = true
      }
      // Kein eigenes "continue": formVon kennt "merge" nicht, der Schritt
      // fällt gleich darunter weg. Die Regel steht damit an genau einer
      // Stelle, und wer sie aufhebt, hebt sie ganz auf.
    } else if (WAHL.has(step.type) && auswege(step) === 0) {
      // Hier gibt es keinen zweiten Weg. Was OSRM meldet, ist eine Kurve.
      merken()
      continue
    }

    const shape = formVon(step)
    if (!shape) {
      merken()
      continue
    }

    const neu = kennungen(quelle)
    // An einem Abzweig ist Geradeausfahren gar nicht vorgesehen: dort steht der
    // Pfeil immer auf dem abgehenden Weg (siehe tulipArms in tulip.ts). Wer
    // geradeaus fährt, hat hier nichts zu notieren.
    if (shape === 'abzweig' && Math.abs(roh) <= GERADE) {
      merken()
      continue
    }
    /*
     * Weiter auf derselben angeschriebenen Strasse ist keine Anweisung. Das
     * gilt auch für den Kreisverkehr, den man geradeaus durchfährt - auf der
     * rumänischen DN5 sind das zehn Ringe hintereinander, an denen jedes Mal
     * "DN5" ausgeschildert ist - und für die Gabelung einer Autobahn, bei der
     * beide Äste dieselbe Nummer tragen. Bei der Gabelung ohne Winkelprüfung:
     * sie teilt sich um wenige Grad, gemessen wäre sie immer geradeaus.
     *
     * "Dieselbe" heisst dabei: es fällt keine der Nummern weg, unter denen man
     * bis hierher fuhr - und nicht bloss, dass die erste noch dieselbe ist.
     * Hinter Basel läuft die Fahrbahn als "A2; A3", und dreizehn Kilometer
     * später steht an der Verzweigung nur noch "A2". Wer allein die erste
     * Nummer vergleicht, sieht dort zweimal A2 und wirft die Zeile weg; wer
     * links bleibt, fährt auf der A3 nach Zürich statt auf der A2 nach Bern.
     *
     * Und das ist kein Sonderfall. Die Schweiz führt ihre Autobahnen
     * streckenweise gemeinsam - A1 und A3 sind dort nicht zwei Strassen
     * nebeneinander, sondern eine Fahrbahn mit zwei Nummern -, und sie trennt
     * sie an einer T-Verzweigung: drei Arme, eine Alternative, vierzehn bis
     * fünfzehn Grad Spreizung. Geometrisch sind die beiden Äste damit nicht zu
     * unterscheiden, und welcher die gemeinsame Nummer behält, wechselt die
     * Seite: in den Antwortdateien liegt er dreimal links und zweimal rechts.
     * Die Nummer auf dem Schild ist an solch einer Stelle nicht ein Hinweis
     * unter mehreren, sondern der einzige Unterschied, den es gibt - genau die
     * Auskunft, die dieser Wächter wegzuwerfen droht.
     *
     * Andersherum ist nichts zu entscheiden: kommt eine Nummer dazu, laufen
     * zwei Strassen zusammen, und man bleibt einfach auf der Fahrbahn. Auf
     * Bern - Bled steht das bei Kilometer 99.7 und 125.9 so in den Daten, auf
     * Bern - Chur bei 98.1 - dort wird aus der A1 die gemeinsame Fahrbahn mit
     * der A3 und der A4.
     */
    if (
      dieselbeStrasse(neu, vorher) &&
      shape !== 'auffahrt' &&
      shape !== 'abfahrt' &&
      (shape === 'gabel' || Math.abs(roh) <= GERADE)
    ) {
      merken()
      continue
    }

    let turn: number
    if (NACH_SEITE.has(shape)) {
      // Die Seite und nicht der Winkel - und nur, wenn sie zu belegen ist.
      const links = seite(step, roh)
      if (links === null) {
        merken()
        continue
      }
      turn = shape === 'gabel' ? (links ? 315 : 45) : links ? 270 : 90
    } else {
      const rast = einrasten(shape, roh)
      if (rast.ab > MAX_ABWEICHUNG) {
        merken()
        continue
      }
      turn = rast.turn
    }

    out.push({
      km,
      shape,
      turn,
      // Die Ausfahrtnummer steht bei beiden Hälften des Kreisverkehrs; fehlt sie
      // beim Verlassen, gilt die der Einfahrt. Sie ist die eigentliche Auskunft
      // eines Kreiselzeichens und darf nicht an einem fehlenden Feld verloren
      // gehen.
      exit: Math.max(0, Math.floor(quelle.exit ?? step.exit ?? 0)),
      name: (quelle.name ?? '').trim(),
      ref: (quelle.ref ?? '').trim(),
      dest: (quelle.destinations ?? '').trim(),
      vorher: vorher[0] ?? '',
    })
    if (neu.length > 0) vorher = neu
  }

  return out
}

/**
 * Der Vorschlag, den ein Einfädeln an diesem Kilometer abschliesst.
 *
 * Gesucht wird rückwärts und nicht nur beim unmittelbar letzten Vorschlag:
 * zwischen Rampe und Einfädeln liegt oft noch die Gabelung des Knotens, und die
 * darf die Zuordnung nicht zerreissen. Zwei Kilometer, weil ein Autobahnkreuz
 * so lang ist; darüber hinaus ist es ein eigener Anschluss.
 *
 * Die Rampe geht der Gabelung vor, auch wenn sie weiter zurückliegt. Das ist
 * dieselbe Rangfolge, die zusammenfassen() ohnehin herstellt - dort fällt die
 * Gabelung neben einer Rampe weg und gibt ihr ihre Beschilderung mit.
 *
 * Gibt es keine Rampe, ist die Gabelung der Knoten: an einer Auffahrt ohne
 * eigene Rampe - der Fall Bled - Bovec, Kilometer 3.9 - ist sie die Stelle, an
 * der man handelt, und das Einfädeln einen halben Kilometer später sagt ihr,
 * dass es auf die A2 geht.
 */
function knotenVor(bisher: readonly Kandidat[], km: number): Kandidat | null {
  let gabel: Kandidat | null = null
  for (let i = bisher.length - 1; i >= 0; i--) {
    const k = bisher[i]!
    if ((km - k.km) * 1000 > 2 * KNOTEN_M) break
    if (k.shape === 'auffahrt' || k.shape === 'abfahrt') return k
    if (k.shape === 'gabel' && !gabel) gabel = k
  }
  return gabel
}

/**
 * Trägt der Rampe nach, was erst am Ende des Anschlusses steht.
 *
 * Ergänzt und überschreibt nicht: was auf dem Schild an der Ausfahrt steht, ist
 * die bessere Auskunft als die Nummer der Strasse, auf der man danach landet -
 * im Auto liest man das Schild.
 */
function ergaenzen(rampe: Kandidat, quelle: { ref?: string; name?: string; dest?: string }): void {
  const nummer = (quelle.ref ?? '').trim()
  // Die Nummer schlägt den Namen: eine Rampe heisst "Tiefenaustrasse", die
  // Autobahn dahinter heisst A1 - und im Roadbook steht, worauf man auffährt,
  // nicht, wie die Schleife dorthin genannt wird.
  if (!kennung(rampe) || (nummer && !rampe.ref)) {
    rampe.ref = nummer
    rampe.name = (quelle.name ?? '').trim()
  }
  if (!rampe.dest) rampe.dest = (quelle.dest ?? '').trim()
}

/**
 * Fasst zusammen, was dieselbe Stelle meint.
 *
 * Zwei Dinge zerfallen in den Daten in mehrere Manöver: eine Kreuzung, vor der
 * eine Abbiegespur beginnt, und ein Autobahnknoten aus Ausfahrt, Gabelung und
 * Einfädeln. Der erste Vorschlag bleibt stehen - er sitzt dort, wo man handelt,
 * und ein Kilometerstand, der hinter der Kreuzung liegt, käme im Auto zu spät.
 */
function zusammenfassen(kandidaten: readonly Kandidat[]): Kandidat[] {
  const nah: Kandidat[] = []
  for (const k of kandidaten) {
    const letzter = nah[nah.length - 1]
    // Die Fähre nie. Vor dem Anleger steht fast immer noch eine Abbiegung -
    // in die Hafeneinfahrt, auf die Warteschlange -, und die läge innerhalb
    // dieser hundertfünfzig Meter. Zusammengelegt bliebe die Abbiegung stehen
    // und die Fähre fiele weg: dieselbe Falle wie beim Tunnel, nur teurer,
    // denn eine Abbiegung sieht man, wenn man davorsteht, eine abgefahrene
    // Fähre nicht.
    if (letzter && (k.km - letzter.km) * 1000 < NAHE_M && k.shape !== 'faehre') continue
    nah.push(k)
  }

  // Eine Gabelung neben einer Rampe ist die Gabelung *der* Rampe. Sie fällt
  // weg, gibt der Rampe aber vorher noch ihre Beschilderung mit: eine
  // Ausfahrtsschleife trägt oft weder Nummer noch Schild, und erst der Ast,
  // auf den sie führt, sagt, wohin es geht.
  const out: Kandidat[] = []
  for (const k of nah) {
    const rampe =
      k.shape === 'gabel'
        ? nah.find(
            (o) =>
              (o.shape === 'auffahrt' || o.shape === 'abfahrt') &&
              Math.abs(o.km - k.km) * 1000 <= KNOTEN_M,
          )
        : undefined
    if (rampe) {
      ergaenzen(rampe, k)
      continue
    }
    out.push(k)
  }
  return out
}

/**
 * Wie schwer ein Vorschlag wiegt.
 *
 * Die Rangfolge ist die des Schadens, den ein verpasster Hinweis anrichtet:
 *
 * 5 - Die Fähre. Sie ist die einzige Zeile des Buchs, die einen Termin meint:
 *     wer sie verpasst, wartet auf die nächste, und das sind Stunden oder ein
 *     Tag. Jede andere verpasste Zeile kostet Kilometer.
 * 4 - Auf- und Abfahrten. Eine verpasste Ausfahrt kostet zwanzig Kilometer bis
 *     zur nächsten Wende, und auf der Autobahn hilft kein Blick aus dem Fenster.
 *     Dazu die Gabelung, an deren Ende man einfädelt: sie ist eine Auffahrt,
 *     auch wenn der Dienst sie "fork" nennt (siehe Kandidat.knoten).
 * 3 - Stellen, an denen es kein Schild gibt, das die Entscheidung bestätigt:
 *     eine Strasse ohne Namen und ohne Nummer, eine T-Kreuzung (geradeaus geht
 *     nicht, und welche Seite es ist, entscheidet man im Vorbeifahren), ein
 *     Kreisverkehr, den man nicht geradeaus verlässt oder erst ab der dritten
 *     Ausfahrt.
 * 2 - Gabelungen und alles, wo die Strasse ihre Nummer wechselt: dort gibt es
 *     ein Schild, aber es will gelesen werden.
 * 1 - der Rest.
 */
function gewicht(k: Kandidat): number {
  if (k.fertig) return k.fertig.weight
  if (k.shape === 'auffahrt' || k.shape === 'abfahrt' || k.knoten) return 4
  if (k.shape === 't' || kennung(k) === '') return 3
  if (k.shape === 'kreisel' && (k.turn !== 0 || k.exit >= 3)) return 3
  if (k.shape === 'gabel' || kennung(k) !== k.vorher) return 2
  return 1
}

/**
 * Kürzt auf den Deckel, ohne die Reihenfolge zu verlieren.
 *
 * Sortiert wird nach Gewicht und bei gleichem Gewicht nach Fahrreihenfolge -
 * dann wird abgeschnitten, und was übrig ist, kommt wieder in Fahrreihenfolge.
 * Eine Etappe, die den Deckel reisst, verliert damit ihre belanglosen Zeilen
 * und behält ihre Autobahnknoten, und zwar an ihrem Platz.
 */
function deckeln(kandidaten: Kandidat[], max: number): Kandidat[] {
  if (max <= 0) return []
  if (kandidaten.length <= max) return kandidaten

  const plaetze = kandidaten
    .map((k, i) => ({ i, g: gewicht(k) }))
    .sort((a, b) => b.g - a.g || a.i - b.i)
    .slice(0, max)
    .map((e) => e.i)
    .sort((a, b) => a - b)

  return plaetze.map((i) => kandidaten[i]!)
}

/**
 * Der Hinweis, der rechts neben dem Zeichen steht.
 *
 * Gebaut aus dem, was der Dienst mitliefert, und in der Reihenfolge, in der man
 * es im Auto braucht: zuerst die Handlung (die Ausfahrtnummer am Kreisel), dann
 * das, was auf dem Schild steht, dann die Strasse. Kurz, weil daneben noch ein
 * Zeichen steht und die Seite nur 128 mm breit ist.
 *
 * Die Rahmensätze sind deutsch und ohne ß - der Betreiber ist Schweizer.
 * Strassennamen und Wegweisertexte werden dagegen unverändert übernommen, auch
 * "Brudermühlstraße": das ist kein Text der Anwendung, sondern das, was auf dem
 * Schild steht, und wer es umschreibt, findet es dort nicht wieder.
 */
function notiz(k: Kandidat): string {
  if (k.fertig) return k.fertig.note
  const strasse = kennung(k)
  const schild = wegweiser(k.dest)
  // Wohin es laut Schild geht, ohne die Strassennummer davor: die steht schon
  // im übrigen Hinweis, und "auf A14, Ri. A14 Bregenz" sagt sie zweimal.
  const ziel = ohneNummer(schild, strasse)
  const teile: string[] = []

  if (k.shape === 'kreisel') {
    if (k.exit > 0) teile.push(`${String(k.exit)}. Ausfahrt`)
    else teile.push('Kreisverkehr')
    if (ziel) teile.push(`Ri. ${ziel}`)
    if (strasse) teile.push(`auf ${strasse}`)
  } else if (k.shape === 'abfahrt') {
    // Hier steht das Schild vollständig, samt Nummer: an einem Autobahnkreuz
    // ist "Ausfahrt A 8 Salzburg" genau das, was über der Fahrbahn hängt.
    teile.push(schild || strasse ? `Ausfahrt ${schild || strasse}` : 'Autobahn verlassen')
  } else if (k.shape === 'auffahrt') {
    teile.push(strasse ? `auf ${strasse}` : `auf ${schild || 'die Autobahn'}`)
    if (ziel) teile.push(`Ri. ${ziel}`)
  } else {
    teile.push(strasse ? `auf ${strasse}` : 'Strasse ohne Namen')
    if (ziel) teile.push(`Ri. ${ziel}`)
  }

  // Angehängt wird nur, was noch ganz hineinpasst: ein abgeschnittener Ortsname
  // ist schlimmer als keiner, denn auf dem Schild steht er ganz.
  let text = teile[0] ?? ''
  for (const teil of teile.slice(1)) {
    const naechst = `${text}, ${teil}`
    if (naechst.length > NOTIZ_MAX) break
    text = naechst
  }
  return text.length > NOTIZ_MAX ? `${text.slice(0, NOTIZ_MAX - 1).trimEnd()}…` : text
}

/**
 * Was auf dem Wegweiser steht, auf eine Zeile gebracht.
 *
 * Der Dienst schreibt Wegweiser als "A 8: Salzburg" oder "Nin, Smilčić, Zadar -
 * zapad" - Nummer vor dem Doppelpunkt, dahinter die Orte, durch Kommas
 * getrennt. Genommen wird der erste Ort: er ist der nächste und steht auf dem
 * Schild zuoberst. Der Doppelpunkt wird zum Leerzeichen, damit die Zeile im
 * Buch nicht nach Programmiersprache aussieht.
 */
function wegweiser(dest: string): string {
  const [kopf = '', ...rest] = dest.split(':')
  const nummer = kopf.split(',')[0]?.trim() ?? ''
  if (rest.length === 0) return nummer

  const orte = rest.join(':').split(',')[0]?.trim() ?? ''
  return orte ? `${nummer} ${orte}`.trim() : nummer
}

/**
 * Der Wegweiser ohne die Strassennummer, die ohnehin schon dasteht.
 *
 * Verglichen wird ohne Leerzeichen: der Dienst schreibt dieselbe Autobahn
 * einmal als "A14" und einmal als "A 14", und zwei Schreibweisen derselben
 * Nummer sind nicht zwei Auskünfte.
 */
function ohneNummer(schild: string, strasse: string): string {
  if (!schild || !strasse) return schild

  const ziel = eng(strasse)
  // Wortweise und nicht am ersten Wort: eine Autobahn heisst "A 96" und
  // besteht damit schon aus zwei Wörtern.
  const teile = schild.split(' ')
  let vorne = ''
  for (let i = 0; i < teile.length; i++) {
    vorne += teile[i]
    if (eng(vorne) === ziel) return teile.slice(i + 1).join(' ')
  }
  return schild
}

/** Eine Strassennummer ohne bedeutungslose Unterschiede der Schreibweise. */
function eng(s: string): string {
  return s.replace(/\s+/g, '').toLowerCase()
}
